/**
 * ProspectosPage — Lista y formulario rápido de prospectos.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip, Paper, Stack, TextField,
  MenuItem, Tooltip, IconButton, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import SlideOver from '../common/SlideOver';
import WhatsAppButton from './WhatsAppButton';
import {
  API_BASE, getEmpresaId, getAuthHeaders,
  PIPELINE_PROSPECTO, TEMPERATURAS,
} from './format';

function PipelineChip({ estado }) {
  const meta = PIPELINE_PROSPECTO.find(p => p.value === estado) || PIPELINE_PROSPECTO[0];
  return <Chip size="small" label={meta.label}
    sx={{ bgcolor: `${meta.color}20`, color: meta.color, fontWeight: 700, fontSize: 10, height: 18 }} />;
}

export default function ProspectosPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [formOpen, setFormOpen] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ empresa_id: getEmpresaId() });
      if (filtroEstado) params.set('pipeline_estado', filtroEstado);
      if (busqueda) params.set('q', busqueda);
      const r = await fetch(`${API_BASE}/turismo/comercial/prospectos?${params}`,
        { headers: getAuthHeaders() });
      const d = await r.json();
      setItems(d.prospectos || []);
    } catch { setItems([]); }
    setLoading(false);
  }, [filtroEstado, busqueda]);
  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Prospectos</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Personas o empresas que aún no son agencia ni cliente registrado.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} disableElevation
          onClick={() => setFormOpen(true)}
          sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)' }}>
          Nuevo prospecto
        </Button>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField size="small" fullWidth placeholder="Buscar por nombre, WhatsApp, correo, ciudad…"
          value={busqueda} onChange={e => setBusqueda(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>) }} />
        <TextField select size="small" sx={{ minWidth: 180 }} label="Estado"
          value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {PIPELINE_PROSPECTO.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
        </TextField>
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <PersonSearchIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Sin prospectos aún</Typography>
        </Paper>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 1.5,
        }}>
          {items.map(p => (
            <Paper key={p.id} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{p.nombre}</Typography>
                <PipelineChip estado={p.pipeline_estado} />
              </Stack>
              <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                {p.tipo} {p.ciudad && `· ${p.ciudad}`}
              </Typography>
              {p.numero_whatsapp && (
                <Typography sx={{ fontSize: 11, color: '#0d9488' }}>
                  📱 {p.numero_whatsapp}
                </Typography>
              )}
              {p.fuente && (
                <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 0.5 }}>
                  Fuente: {p.fuente}{p.fuente_detalle && ` · ${p.fuente_detalle}`}
                </Typography>
              )}
              {p.numero_whatsapp && (
                <Box sx={{ mt: 1 }}>
                  <WhatsAppButton
                    numero={p.numero_whatsapp}
                    nombreContacto={p.nombre}
                    empresaNombre="Entretur"
                    aplicaA="prospecto"
                    variant="outlined"
                  />
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {formOpen && (
        <ProspectoForm open={formOpen} onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); cargar(); }} />
      )}
    </Box>
  );
}

function ProspectoForm({ open, onClose, onSaved }) {
  const [form, setForm] = useState({
    tipo: 'turista', nombre: '', numero_whatsapp: '', correo: '', telefono: '',
    ciudad: '', pais: '', fuente: '', fuente_detalle: '',
    pipeline_estado: 'prospecto', temperatura: 'media',
    notas: '', valor_estimado_mes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setError(null);
    if (!form.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const body = {
        empresa_id: getEmpresaId(),
        ...form,
        valor_estimado_mes: form.valor_estimado_mes ? Number(form.valor_estimado_mes) : null,
      };
      const r = await fetch(`${API_BASE}/turismo/comercial/prospectos`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Error'); }
      onSaved?.();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <SlideOver open={open} onClose={onClose} variant="wide"
      title="Nuevo prospecto"
      subtitle="Persona o empresa que aún no compra"
      icon={<PersonSearchIcon />}
      footer={
        <>
          <Button onClick={onClose} startIcon={<CloseIcon />} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" disableElevation onClick={handleSave}
            disabled={saving} startIcon={<SaveIcon />}
            sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)' }}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField select size="small" fullWidth label="Tipo" value={form.tipo}
            onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
            <MenuItem value="turista">Turista directo</MenuItem>
            <MenuItem value="hotel">Hotel</MenuItem>
            <MenuItem value="agencia_nueva">Agencia nueva</MenuItem>
            <MenuItem value="otro">Otro</MenuItem>
          </TextField>
          <TextField size="small" fullWidth label="Nombre*" value={form.nombre}
            onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField size="small" fullWidth label="WhatsApp" value={form.numero_whatsapp}
            onChange={e => setForm(f => ({ ...f, numero_whatsapp: e.target.value }))} />
          <TextField size="small" fullWidth label="Correo" value={form.correo}
            onChange={e => setForm(f => ({ ...f, correo: e.target.value }))} />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField size="small" fullWidth label="Ciudad" value={form.ciudad}
            onChange={e => setForm(f => ({ ...f, ciudad: e.target.value }))} />
          <TextField size="small" fullWidth label="País" value={form.pais}
            onChange={e => setForm(f => ({ ...f, pais: e.target.value }))} />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField select size="small" fullWidth label="Fuente"
            value={form.fuente} onChange={e => setForm(f => ({ ...f, fuente: e.target.value }))}>
            <MenuItem value="">—</MenuItem>
            <MenuItem value="aeropuerto">Aeropuerto</MenuItem>
            <MenuItem value="hotel">Hotel</MenuItem>
            <MenuItem value="feria">Feria/evento</MenuItem>
            <MenuItem value="referido">Referido</MenuItem>
            <MenuItem value="redes">Redes</MenuItem>
            <MenuItem value="otro">Otro</MenuItem>
          </TextField>
          <TextField size="small" fullWidth label="Fuente detalle"
            placeholder="Ej. Hotel Movich"
            value={form.fuente_detalle}
            onChange={e => setForm(f => ({ ...f, fuente_detalle: e.target.value }))} />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField select size="small" fullWidth label="Estado"
            value={form.pipeline_estado}
            onChange={e => setForm(f => ({ ...f, pipeline_estado: e.target.value }))}>
            {PIPELINE_PROSPECTO.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" fullWidth label="Temperatura"
            value={form.temperatura}
            onChange={e => setForm(f => ({ ...f, temperatura: e.target.value }))}>
            {TEMPERATURAS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
        </Stack>
        <TextField size="small" fullWidth type="number" label="Valor estimado mensual (COP)"
          value={form.valor_estimado_mes}
          onChange={e => setForm(f => ({ ...f, valor_estimado_mes: e.target.value }))} />
        <TextField size="small" fullWidth multiline minRows={3} label="Notas"
          value={form.notas}
          onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
        {error && <Typography sx={{ fontSize: 12, color: '#dc2626' }}>{error}</Typography>}
      </Stack>
    </SlideOver>
  );
}
